import { LoginCredentials, AuthResponse, User } from '../types';
import api from './api';

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse | undefined> {
    try {
      // Send all required fields as expected by the API
      const loginData = {
        username: credentials.username,
        password: credentials.password,
        appVersion: '1.0.0',
        uuID: null,
        deviceName: 'Web Portal',
        devicePlatform: 'web',
        deviceManufacturer: 'ASMonitor',
        orgID: credentials.orgID || null
      };

      const response = await api.post('/db/auth/login', loginData);
      
      
      // Handle the response based on the API structure
      if (response.data.accessToken) {
        // Check if user has multiple organizations
        if (response.data.organizations && response.data.organizations.length > 1) {
          return {
            accessToken: response.data.accessToken,
            user: null,
            organizations: response.data.organizations,
            message: response.data.message || 'Bitte wählen Sie eine Organisation aus'
          };
        } else {
          // Single organization or no organizations - create user from credentials
          
          // Extract orgID from JWT token if available
          let orgID = 0;
          let orgName = 'Unknown Org';
          
          try {
            const tokenPayload = JSON.parse(atob(response.data.accessToken.split('.')[1]));
            orgID = tokenPayload.orgID || 0;
          } catch (error) {
            // Could not extract orgID from token
          }
          
          // Try to get organization name from the response
          if (response.data.organizations && response.data.organizations.length === 1) {
            const org = response.data.organizations[0];
            orgName = org.fdName || org.orgName || org.name || 'Unknown Org';
          } else if (response.data.user) {
            // If user object is provided in response
            orgName = response.data.user.fdName || response.data.user.orgName || 'Unknown Org';
          } else if (response.data.organization) {
            // Organization info is directly in the response
            orgName = response.data.organization.name || 'Unknown Org';
          }
          
          const user: User = {
            id: response.data.user?.id || 0,
            username: credentials.username,
            role: 'user',
            orgID: orgID,
            orgName: orgName,
            fdName: orgName, // Also set fdName for consistency
            firstname: response.data.user?.firstname || response.data.user?.firstName || '',
            lastname: response.data.user?.lastname || response.data.user?.lastName || '',
            email: response.data.user?.email || '',
            roles: response.data.user?.roles || response.data.roles || [],
            permissions: response.data.user?.permissions || response.data.permissions || {
              'firehub-super-admin': false,
              'asm-login-permission': false,
              'asm-settings-permission': false,
              'asm-manage-users': false,
              'asm-manage-roles': false,
              'asm-manage-analytics': false,
              'asm-manage-reports': false,
              'asm-manage-operations': false
            }
          };
          
          // Check if user has login permission
          console.log('User permissions:', user.permissions);
          const hasLoginPermission = user.permissions?.['firehub-super-admin'] || user.permissions?.['asm-login-permission'];
          console.log('Has login permission:', hasLoginPermission);
          if (!hasLoginPermission) {
            console.log('Login permission denied, throwing error');
            throw new Error('Sie haben keine Berechtigung, sich anzumelden. Bitte wenden Sie sich an Ihren Administrator.');
          }
          
          return {
            accessToken: response.data.accessToken,
            user,
            refreshToken: response.data.refreshToken
          };
        }
      }
      
      throw new Error('Invalid response from server');
    } catch (error: any) {
      // Check if it's a permission error (user exists but no login permission)
      if (error.message && error.message.includes('Berechtigung')) {
        // Redirect to login page with error message
        const errorMessage = encodeURIComponent(error.message);
        window.location.href = `/login?error=${errorMessage}`;
        return;
      }
      
      // For all other errors (including user not found), give generic message
      const genericMessage = 'Ungültige Anmeldedaten';
      const errorMessage = encodeURIComponent(genericMessage);
      window.location.href = `/login?error=${errorMessage}`;
      return;
    }
  },

  async selectOrganization(orgID: number, credentials: LoginCredentials): Promise<AuthResponse | undefined> {
    try {
      const tempAccessToken = localStorage.getItem('tempAccessToken');
      const tempRefreshToken = localStorage.getItem('tempRefreshToken');
      
      if (!tempAccessToken) {
        throw new Error('Keine temporären Tokens verfügbar');
      }
      
      // We need to get the original credentials from the login process
      // For now, we'll use the tempAccessToken to make the request
      const tempApi = api; // Use the centralized api instance
      
      // The endpoint expects username, password, and selectedOrgID
      const requestBody = { 
        selectedOrgID: orgID,
        username: credentials.username,
        password: credentials.password,
        appVersion: '1.0.0',
        uuID: null,
        deviceName: 'Web Portal',
        devicePlatform: 'web',
        deviceManufacturer: 'ASMonitor'
      };
      
      const response = await tempApi.post('/db/auth/login-final', requestBody);

      
      if (response.data.accessToken) {
        // Extract user info from the token or response
        let userInfo = null;
        let orgName = 'Unknown Org';
        
        try {
          const tokenPayload = JSON.parse(atob(response.data.accessToken.split('.')[1]));
          userInfo = {
            id: tokenPayload.id || 0,
            username: tokenPayload.username || credentials.username,
            role: 'user',
            orgID: tokenPayload.orgID || orgID
          };
          
          // Try to get organization name from the organizations list stored in localStorage
          const organizationsData = localStorage.getItem('organizations');
          if (organizationsData) {
            try {
              const organizations = JSON.parse(organizationsData);
              const selectedOrg = organizations.find((org: any) => org.orgID === orgID);
              if (selectedOrg) {
                orgName = selectedOrg.fdName || selectedOrg.orgName || selectedOrg.name || 'Unknown Org';
              }
            } catch (error) {
              // Could not parse organizations data
            }
          }
          
        } catch (error) {
          // Could not extract user info from token
          userInfo = {
            id: 0,
            username: credentials.username,
            role: 'user',
            orgID: orgID
          };
        }
        
        const user: User = {
          id: userInfo.id,
          username: userInfo.username,
          role: userInfo.role,
          orgID: userInfo.orgID,
          orgName: orgName,
          fdName: orgName, // Also set fdName for consistency
          roles: response.data.user?.roles || response.data.roles || [],
          permissions: response.data.user?.permissions || response.data.permissions || {
            'firehub-super-admin': false,
            'asm-login-permission': false,
            'asm-settings-permission': false,
            'asm-manage-users': false,
            'asm-manage-roles': false,
            'asm-manage-analytics': false,
            'asm-manage-reports': false,
            'asm-manage-operations': false
          }
        };
        
        // Check if user has login permission
        console.log('User permissions (selectOrg):', user.permissions);
        const hasLoginPermission = user.permissions?.['firehub-super-admin'] || user.permissions?.['asm-login-permission'];
        console.log('Has login permission (selectOrg):', hasLoginPermission);
        if (!hasLoginPermission) {
          console.log('Login permission denied (selectOrg), throwing error');
          throw new Error('Sie haben keine Berechtigung, sich anzumelden. Bitte wenden Sie sich an Ihren Administrator.');
        }
        
        return {
          accessToken: response.data.accessToken,
          user,
          refreshToken: response.data.refreshToken
        };
      }
      
      throw new Error('Invalid response from server');
    } catch (error: any) {
      // Check if it's a permission error (user exists but no login permission)
      if (error.message && error.message.includes('Berechtigung')) {
        // Redirect to login page with error message
        const errorMessage = encodeURIComponent(error.message);
        window.location.href = `/login?error=${errorMessage}`;
        return;
      }
      
      // For all other errors (including user not found), give generic message
      const genericMessage = 'Ungültige Anmeldedaten';
      const errorMessage = encodeURIComponent(genericMessage);
      window.location.href = `/login?error=${errorMessage}`;
      return;
    }
  },

  async getCurrentUser(): Promise<User> {
    try {
      const response = await api.get('/db/auth/me');

      
      const userData = response.data;
      
      // Ensure the user object has the proper structure
      return {
        id: userData.id || 0,
        username: userData.username || '',
        role: userData.role || 'user',
        orgID: userData.orgID || 0,
        orgName: userData.orgName || userData.fdName || 'Unknown Org',
        fdName: userData.fdName || userData.orgName || 'Unknown Org',
        firstname: userData.firstname || userData.firstName || '',
        lastname: userData.lastname || userData.lastName || '',
        email: userData.email || '',
        roles: userData.roles || [],
        permissions: userData.permissions || {
          'firehub-super-admin': false,
          'asm-login-permission': false,
          'asm-settings-permission': false,
          'asm-manage-users': false,
          'asm-manage-roles': false,
          'asm-manage-analytics': false,
          'asm-manage-reports': false,
          'asm-manage-operations': false
        }
      };
    } catch (error: any) {
      console.warn('Could not fetch user data from /db/auth/me, using fallback:', error.message);
      
      // Fallback: try to get user data from localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          return {
            ...user,
            roles: user.roles || [],
            permissions: user.permissions || {
              'firehub-super-admin': false,
              'asm-login-permission': false,
              'asm-settings-permission': false,
              'asm-manage-users': false,
              'asm-manage-roles': false,
              'asm-manage-analytics': false,
              'asm-manage-reports': false,
              'asm-manage-operations': false
            }
          };
        } catch (parseError) {
          console.error('Could not parse stored user data:', parseError);
        }
      }
      
      // Final fallback: return basic user structure
      return {
        id: 0,
        username: 'Unknown User',
        role: 'user',
        orgID: 0,
        orgName: 'Unknown Org',
        fdName: 'Unknown Org',
        roles: [],
        permissions: {
          'firehub-super-admin': false,
          'asm-login-permission': false,
          'asm-settings-permission': false,
          'asm-manage-users': false,
          'asm-manage-roles': false,
          'asm-manage-analytics': false,
          'asm-manage-reports': false,
          'asm-manage-operations': false
        }
      };
    }
  },

  async refreshToken(): Promise<AuthResponse> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await api.post('/db/auth/refresh', { refreshToken });
      return response.data;
    } catch (error: any) {
      throw new Error('Token-Erneuerung fehlgeschlagen');
    }
  },
}; 